import { ICONS } from '@fl/cmsch-fe-library';
import React, { FC, memo, PropsWithChildren, useCallback } from 'react';
import { AntLayout } from 'common/ant';
import { MainMenuMemoizedWrapper } from './main-menu-memoized-wrapper';
import styles from './styles.sass';

/** Props for the responsive shell layout that owns the sider and header menu placement. */
interface Props {
  /** Whether the viewport currently shows the side menu breakpoint. */
  isSiderActive: boolean;
  /** Whether the side menu is collapsed. */
  isSideMenuCollapsed: boolean;
  /** Hides the header in E2E mode where screenshots need a stable layout. */
  isE2E: boolean;
  /** Collapses the side menu when the backdrop is clicked. */
  setIsSideMenuCollapsed(isCollapsed: boolean): void;
  /** Tracks breakpoint transitions from the Ant layout sider. */
  setIsSiderActive(isActive: boolean): void;
}
const MenuLayoutBase: FC<PropsWithChildren<Props>> = props => {
  const {
    isSiderActive,
    isSideMenuCollapsed,
    children,
    isE2E,
    setIsSideMenuCollapsed,
    setIsSiderActive
  } = props;
  const handleSideMenuCollapse = useCallback(() => {
    setIsSideMenuCollapsed(true);
  }, [isSideMenuCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AntLayout className={styles.menuAppWrapperLayout} hasSider={false} data-sentry-element="AntLayout" data-sentry-component="MenuLayoutBase" data-sentry-source-file="menu-layout.tsx">
            <AntLayout.Sider width="300px" breakpoint="sm" collapsedWidth="0" collapsed={isSideMenuCollapsed} onBreakpoint={setIsSiderActive} onCollapse={setIsSideMenuCollapsed} className={isSiderActive ? styles.menuAppWrapperSiderActive : 'd-none'} trigger={ICONS[isSideMenuCollapsed ? 'barsOutlined' : 'closeOutlined']} data-test-id="menu-layout-sider" data-sentry-element="unknown" data-sentry-source-file="menu-layout.tsx">
                <MainMenuMemoizedWrapper menuType="side" setIsSideMenuCollapsed={setIsSideMenuCollapsed} isSideMenuCollapsed={isSideMenuCollapsed} data-sentry-element="MainMenuMemoizedWrapper" data-sentry-source-file="menu-layout.tsx" />
            </AntLayout.Sider>
            {isSiderActive && !isSideMenuCollapsed && <div className={styles.menuAppWrapperSiderModalBackdrop} onClick={handleSideMenuCollapse} />}
            <AntLayout.Header className={isE2E ? 'd-none' : 'p-0'} data-test-id="menu-layout-header" data-sentry-element="unknown" data-sentry-source-file="menu-layout.tsx">
                <MainMenuMemoizedWrapper menuType={isSiderActive ? 'small' : 'full'} setIsSideMenuCollapsed={setIsSideMenuCollapsed} isSideMenuCollapsed={isSideMenuCollapsed} data-sentry-element="MainMenuMemoizedWrapper" data-sentry-source-file="menu-layout.tsx" />
            </AntLayout.Header>
            {children}
        </AntLayout>;
};

/**
 * Renders the shared application chrome around the active route content.
 *
 * @param props Layout state and the shell content tree.
 * @returns The shell layout containing header, sider, and routed content.
 *
 * @example
 * <MenuLayout
 *     isE2E={false}
 *     isSideMenuCollapsed={false}
 *     isSiderActive={false}
 *     setIsSideMenuCollapsed={setIsSideMenuCollapsed}
 *     setIsSiderActive={setIsSiderActive}
 * >
 *     <AppRoutes />
 * </MenuLayout>
 */
export const MenuLayout = memo(MenuLayoutBase);